import {
  useEffect,
  useMemo,
  useState,
  useContext,
  useCallback,
  useRef,
} from 'react';

import { SearchQuery } from '@keaze/web/common/interfaces';
import { usePagination } from '@keaze/web/hooks';
import { useGetSearchDataQuery } from '@keaze/web/hooks/queries/search/search';
import {
  useSearchQueryParams,
  // useABTestPersonalizedSearch,
  usePersonalizedSearch,
  useSortBy,
  UseMaxPrice,
} from '../hooks';

import { FiltersState, useStateFilters } from '../filters';
import { GTMSearch } from '@keaze/web/libs/GTM';
import { AuthContext } from '@keaze/web/contexts/auth';
import { GetSearchDataParams } from '@keaze/web/api/search';
import { MonthlyCosts } from '@keaze/web/components/homestepper/MonthlyCostDropDown/monthlyCost';

export const DEFAULT_PER_PAGE = 12;
export const MAX_NUMBER_OF_ITEMS_TO_DISPLAY_MORE_RESULTS = 3;

export const DEFAULT_SEARCH_DATA: SearchQuery = {
  data: [],
  pagination: {
    currentPage: 0,
    perPage: 0,
    from: 0,
    to: 0,
    total: 0,
    lastPage: 0,
  },
  sortOptions: [],
  facets: [],
  links: {
    first: '',
    last: '',
    prev: null,
    next: null,
  },
  center: {
    lat: 0,
    lng: 0,
  },
  rectBounds: {
    north: 0,
    east: 0,
    south: 0,
    west: 0,
  },
  clearLocation: false,
};

type OnSortingSelector = (value: string) => void;

type OnChangeWithPersonalizedResults = (value: boolean) => void;

type ExpectedUseSearch = {
  // isPersonalizedSearchButton: boolean;
  isError: boolean;
  isLoading: boolean;
  withPersonalizedBanner: boolean;
  searchData: SearchQuery;
  sortBy: string;
  monthlyCostValue: string;
  page: number;
  onSearchFiltersChange: (filters: FiltersState) => void;
  onSortingSelector: OnSortingSelector;
  onChangePagination: (_: any, page: any) => void;
  onChangeWithPersonalizedResults: OnChangeWithPersonalizedResults;
};

type UseSearch = (dataSize?: number | null) => ExpectedUseSearch;

export const useSearch: UseSearch = (dataSize = null) => {
  const [maxmMnthlyCosts, setmaxmMnthlyCosts] = useState('');

  const { token } = useContext(AuthContext);
  // const {
  //   isPersonalizedSearchBanner,
  //   isPersonalizedSearchButton,
  // } = useABTestPersonalizedSearch();
  const { personalizedSearchData, isPersonalizedSearchData } =
    usePersonalizedSearch();
  const [withPersonalizedResults, setWithPersonalizedResults] =
    useState<boolean>(isPersonalizedSearchData);

  type UseMaxPriceExpected = {
    maxPrice: string;
    onMaxPriceChange: (maxmMnthlyCosts: string) => void;
  };

  type UseMaxPrice = () => UseMaxPriceExpected;

  const { page, onPageChange } = usePagination();
  const { sortBy, onSortByChange } = useSortBy();
  const { monthlyCostValue } = UseMaxPrice();
  const { filters, setFilters } = useStateFilters();
  const { type: occupancyType } = useSearchQueryParams();
  const prevOccupancyType = useRef(occupancyType);

  const generatedSize = useMemo(() => {
    // if (isPersonalizedSearchData || !isPersonalizedSearchBanner) {
    return DEFAULT_PER_PAGE;
    // }

    // return DEFAULT_PER_PAGE -1  ;
  }, [isPersonalizedSearchData]);

  useEffect(() => {
    if (prevOccupancyType.current !== occupancyType) {
      prevOccupancyType.current = occupancyType;

      onPageChange(1);
    }
  }, [prevOccupancyType, occupancyType, onPageChange]);

  const searchDataParams: GetSearchDataParams = useMemo(() => {
    const defaultParams = {
      token,
      size: dataSize ?? generatedSize,
      sort_by: sortBy,
      monthly_spend: monthlyCostValue,
      page,
      ...filters,
      ...(withPersonalizedResults && personalizedSearchData),
    };

    return defaultParams;
  }, [
    token,
    generatedSize,
    dataSize,
    filters,
    sortBy,
    monthlyCostValue,
    page,
    withPersonalizedResults,
    personalizedSearchData,
    maxmMnthlyCosts,
    MonthlyCosts,
  ]);
  const {
    data: searchData,
    isLoading: isSearchDataLoading,
    isFetching: isSearchDataFetching,
    isError,
  } = useGetSearchDataQuery(searchDataParams, {
    initialData: DEFAULT_SEARCH_DATA,
    keepPreviousData: true,
  });

  const handleSearchFiltersChange = useCallback(
    (filters: FiltersState) => {
      setFilters(filters);
      onPageChange(1);
    },
    [setFilters, onPageChange]
  );

  const handleSortingSelector: OnSortingSelector = (value) => {
    onSortByChange(value);
    onPageChange(1);
  };

  const handleChangeWithPersonalizedResults: OnChangeWithPersonalizedResults =
    useCallback(
      (value) => {
        onPageChange(1);
        setWithPersonalizedResults(value);
      },
      [onPageChange]
    );

  const handleChangePagination = (_: any, page: any) => {
    onPageChange(page);

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return {
    // isPersonalizedSearchButton,
    isError,
    isLoading: isSearchDataLoading || isSearchDataFetching,
    withPersonalizedBanner: !isPersonalizedSearchData,
    searchData: searchData as SearchQuery,
    sortBy,
    monthlyCostValue,
    page,
    onSearchFiltersChange: handleSearchFiltersChange,
    onSortingSelector: handleSortingSelector,
    onChangePagination: handleChangePagination,
    onChangeWithPersonalizedResults: handleChangeWithPersonalizedResults,
  };
};
